const _ = require('lodash');
const dotProp = require('dot-prop');
import { Constant } from '../const';
import { getPromptVoiceChannel } from './functions';
import Utility from './Utility';
import inputsToSpeechSensitivityMap from './InputsToSpeechSensitivity';
import inputTypes from './InputTypes';
import nodeTypeMap from './NodeTypeMap';
import { nodeRemoveNullish } from './parse/parseNode';
import {
    DesignChannelType,
    NodeTypeName
} from "../types";

/**
 * JSDoc imports.
 *
 * @import {
 *   Design,
 *   DesignModuleLink,
 *   DesignModuleNode,
 *   DesignModuleNodeProps,
 *   DesignModuleVersion,
 *   DesignObject
 * } from '../types';
 */

export class ExportManager {

    /** @param {Design} serialized */
    getExport(serialized) {
        const timeId = 'Get Export';
        console.time(timeId);

        //DEVDES2-1661 - Initializing these objects outside of the class doesn't allow
        // the replacement of old scripts without page refresh
        const scripts = {
            customFunctions: [],
            transitionFunctions: []
        };

        const variables = {
            "appVariables": [],
            "modVariables": [],
            "convVariables": []
        };
        
        const exclude = {
            "NoteNodeModel": true,
            "BaseNodeModel": true
        };
        
        // begin creating export
        const exportObj = {};
        
        exportObj.application = serialized.client + serialized.application;
        exportObj.branchName = serialized.branchName;
        // TODO: replace hard-coded designer with @see Design schema version once we have that defined.
        exportObj.designer = '1.9';
        exportObj._id = serialized._id;
        exportObj.client = serialized.client;
        exportObj.version = serialized.version;
        exportObj.applicationVersion = serialized.applicationVersion;
        exportObj.rootModule = serialized.rootModule;
        exportObj.dtmfLiveAgentOverridePattern = serialized.dtmf_live_agent_override_pattern;
        exportObj.languages = serialized.languages;
        exportObj.noInputTimeout =  Number(serialized.noInputTimeout);
        exportObj.globalInputHandling = [];
        exportObj.globalDTMFHandling = [];
        exportObj.functions = [];
        exportObj.graphQLSchemas = [];
        exportObj.csvData = [];
        exportObj.commonModules = [];
        const { isOmniChannel } = serialized.omniChannel;
        exportObj.isOmniChannel = isOmniChannel;
        exportObj.variables = variables;
        exportObj.globalExceptionHandling = {
            exception: null,
            timeout: null
        };

        /** @param {DesignModuleLink | DesignModuleNode | DesignObject} item */
        const addScript = (item) => {
            let id;
            let baseInputs;
            let key;
            let script;

            if (item._class === 'BaseLinkModel') {
                id = Utility.uuid4();
                const { inputs, serializedCondition } = item.props.config ?? {};
                item.condition = serializedCondition !== "(())" ? serializedCondition : "true";
                if (inputs) {
                    item.inputs = inputs;
                }
                baseInputs = inputs;
                key = 'transitionFunctions';
                script = item.condition;
            } else {
                id = this.replaceCustomId(item.id);
                baseInputs = item.props.config.inputs;
                key = 'customFunctions';
                script = item.props.config.function?.script;
            }

            if (script) {
                const inputs = baseInputs?.map(input => {
                    return {
                        argumentName: input.argumentName,
                        type: input.type
                    };
                }) ?? [];
                const scriptKey = script + JSON.stringify(inputs);
                if (!scripts[key][scriptKey]) {
                    scripts[key][scriptKey] = {
                        id,
                        inputs,
                        functionType: "CUSTOM",
                        script
                    };
                }

                // Use scriptKey to find ID in case it was generated by a prior node.
                item.scriptId = scripts[key][scriptKey].id;
            }

            return item.scriptId;
        };

        const {
            lastLocalChange: _0,
            ...rootSettings
        } = serialized.rootSettings;
        exportObj.appProperties = {
            ...rootSettings
        };

        const { noInputTimeoutOverride } = exportObj.appProperties;
        Object.entries(noInputTimeoutOverride ?? {}).forEach(([channel, override]) => {
            if (!override) {
                delete noInputTimeoutOverride[channel];
            }
        });

        if (serialized.intentHandling) {
            const inARowExcluded = Utility.getInARowExcludedIntentIds(serialized.components.intents);
            const xAndOutExcluded = Utility.getXAndOutExcludedIntentIds(serialized.components.intents);
            const inARowIntents = [];
            const xAndOutIntents = [];
            const exemptedIntents = ['YES', 'NO', 'WAIT', 'BACK_FROM_WAIT', 'DTMF'];
            const uniqueIntents= Utility.getUniqueIntents(serialized.components.intents);
            uniqueIntents.filter(int => exemptedIntents.indexOf(int.name) === -1).forEach(intent => {
                if (!inARowExcluded.some(iar => iar === intent.id)) {
                    inARowIntents.push(intent.id);
                }
                if (!xAndOutExcluded.some(iar => iar === intent.id)) {
                    xAndOutIntents.push(intent.id);
                }
            });
            if (serialized.intentHandling.inARowLimit) {
                exportObj.appProperties.inARowHandling = {
                    intentIds: inARowIntents,
                    intents: [],
                    limit: parseInt(serialized.intentHandling.inARowLimit),
                    handlingModuleId: serialized.globalLoopHandling.inARow
                }
            }
            if (serialized.intentHandling.xAndOutLimit) {
                exportObj.appProperties.xAndOutHandling = {
                    intentIds: xAndOutIntents,
                    intents: [],
                    limit: parseInt(serialized.intentHandling.xAndOutLimit),
                    handlingModuleId: serialized.globalLoopHandling.xAndOut
                }
            }
        }

        if (serialized.globalErrorHandling) {
            exportObj.globalIntentPreProcessing = serialized.globalErrorHandling.errorHandling;
            Object.assign(exportObj, _.pick(serialized.globalErrorHandling, [
                'postProcessingCustom',
                'returningFromWaitModule',
                'sessionStart',
                'userHangUp'
            ]));

            const postProcessingCustom = (!exportObj.postProcessingCustom) ? null : (
                serialized.components.objects.find((obj) => {
                    return obj.id === exportObj.postProcessingCustom
                })
            );
            if (postProcessingCustom) {
                exportObj.postProcessingCustom = addScript(_.cloneDeep(postProcessingCustom));
            }
        }
        if (serialized.globalIntents) {
            exportObj.globalInputHandling = JSON.parse(JSON.stringify(this.deleteTimeStamps(serialized.globalIntents)));
            exportObj.globalInputHandling = this.getGlobalHandling(exportObj.globalInputHandling);
        }
        if (serialized.compoundIntents) {
            exportObj.compoundIntents = JSON.parse(JSON.stringify(this.deleteTimeStamps(serialized.compoundIntents)));
        }

        exportObj.fillerPrompts = this.removeFillerPromptsTimeStamps(serialized);

        if (serialized.graphQLSchemas) {
            for (var gq = 0; gq < serialized.graphQLSchemas.length; gq++) {
                delete serialized.graphQLSchemas[gq].createdTime;
                delete serialized.graphQLSchemas[gq].lastLocalChange;
                delete serialized.graphQLSchemas[gq].lastSavedChange;
                delete serialized.graphQLSchemas[gq].moduleApp;
                delete serialized.graphQLSchemas[gq].version;
                delete serialized.graphQLSchemas[gq].moduleId;
            }
            exportObj.graphQLSchemas = JSON.parse(JSON.stringify(serialized.graphQLSchemas));
        }
        if (serialized.csvData) {
            for (var cs = 0; cs < serialized.csvData.length; cs++) {
                delete serialized.csvData[cs].createdTime;
                delete serialized.csvData[cs].lastLocalChange;
                delete serialized.csvData[cs].lastSavedChange;
                delete serialized.csvData[cs].moduleApp;
                delete serialized.csvData[cs].version;
                delete serialized.csvData[cs].moduleId;
                serialized.csvData[cs].file = serialized.csvData[cs].name + ".csv";
                delete serialized.csvData[cs].value;
            }
            exportObj.csvData = JSON.parse(JSON.stringify(serialized.csvData));
        }
        if (serialized.globalExceptionHandling) {
            exportObj.globalExceptionHandling = JSON.parse(JSON.stringify(serialized.globalExceptionHandling));
        }

        if (serialized.appProperties.length) {
            exportObj.appProperties.callEncryption = serialized.appProperties.find(p => p.type === "callEncryption");
            exportObj.appProperties.recordSensitiveUtterances = serialized.recordSensitiveUtterances;
            if (exportObj.appProperties.callEncryption) {
                if (exportObj.appProperties.callEncryption.enabled === false) {
                    exportObj.appProperties.callEncryption.pubKeyNameProperty = null;
                    exportObj.appProperties.callEncryption.pubKeyUrlProperty = null;
                }
                delete exportObj.appProperties.callEncryption.type;
                delete exportObj.appProperties.callEncryption.config;
                delete exportObj.appProperties.callEncryption.createdTime;
                delete exportObj.appProperties.callEncryption.lastLocalChange;
                delete exportObj.appProperties.callEncryption.lastSavedChange;
            }

            // TODO: have not tested yet: appProperties is not available on the D2 sample app
            exportObj.appProperties.propertyMappings = serialized.appProperties.filter(p => p.type === "variableBinding").map(ap => {
                let value;
                const variable = Utility.getVariable(ap.variable.split(".")[1], null, serialized);
                if (variable) {
                    switch (variable.props.config.type) {
                        case "BOOLEAN":
                            value = Utility.parseBool(ap.default);
                            break;
                        case "NUMBER":
                            value = Number(ap.default);
                            break;
                        default:
                            value = ap.default ? ap.default : null;
                    }
                    return {
                        propertyName: ap.config,
                        propertyValue: value,
                        variableBinding: ap.variable
                    };
                }
                
            });
        }

        // CORE-36247: Populate globalDTMFHandling
        exportObj.globalDTMFHandling = this.getGlobalDTMFHandling(serialized.components.intents);

        exportObj.components = [];

        // TODO: deleteDisabledModules and deletedDisabledInFlowItems: wrong use of map method
        serialized = this.deleteDisabledModules(serialized);
        serialized = this.deletedDisabledInFlowItems(serialized);

        for (var i = 0; i < serialized.components.modules.length; i++) {
            
            const mod = Utility.updateLocalVariableSensitiveData(serialized.components.modules[i], serialized);
            const newMod = {
                type: this.getType(mod._class),
                name: mod.name,
                id: mod.id,
                props: this.exportProps(mod.props, serialized),
                flow: {
                    beginNode: null,
                    nodes: [],
                    connections: [],
                },
                localVariables: []
            };
            
            // TODO: parameters on modules?
            // if (newMod.props.config.parameters !== undefined) {
            //     for (var inp = 0; inp < newMod.props.config.parameters.length; inp++) {
            //         if (newMod.props.config.parameters[inp].type === "BOOLEAN" && newMod.props.config.parameters[inp].value !== null) {
            //             if (newMod.props.config.parameters[inp].value.toLowerCase() === "true") {
            //                 newMod.props.config.parameters[inp].value = true;
            //             }
            //             else if (newMod.props.config.parameters[inp].value.toLowerCase() === "false") {
            //                 newMod.props.config.parameters[inp].value = false;
            //             }
            //         }
            //         if (newMod.props.config.parameters[inp].type === "NUMBER" && newMod.props.config.parameters[inp].value !== null) {
            //             newMod.props.config.parameters[inp].value = Number(newMod.props.config.parameters[inp].value);
            //         }
            //         newMod.props.config.parameters[inp].type = "VALUE";
            //         newMod.props.config.parameters[inp].argumentName = "param." + newMod.props.config.parameters[inp].varName;
            //         newMod.props.config.parameters[inp].varName = null;
            //     }
            //     newMod.props.config.inputs = newMod.props.config.parameters;
            //     delete newMod.props.config.parameters;
            // }

            if (mod?.localVariables.length > 0) {
                mod.localVariables.forEach(localVar => {
                    const lv = _.cloneDeep(localVar);

                    lv.props.config.defaultValue ??= lv.props.config.defalutValue;
                    delete lv.props.config.defalutValue;

                    if (lv.props.config.type === "PROMPT" || lv.props.config.type === "MODULE") {
                        lv.props.config.type = "STRING";
                    }
                    // TODO: clean up data error, localVariables should not have lastLocalChange as direct property
                    if (lv.lastLocalChange) {
                        delete lv.lastLocalChange;
                    }
                    
                    lv.props = this.exportProps(lv.props, serialized);
                    lv.type = this.getType(lv._class);
                    lv.props.config.readOnly = lv.props.config.constant ? lv.props.config.constant : false;
                    // CORE-36404: Fix local variables with type boolean but strings for defaultValue
                    if(typeof lv.props.config.defaultValue !== 'boolean' 
                           && lv.props.config.type === 'BOOLEAN'){
                        lv.props.config.defaultValue = lv.props.config.defaultValue === "true" ? true : false;
                    }

                    delete lv._class;
                    newMod.localVariables.push(lv);
                    var varprops = {

                        "id": lv.id,
                        "name": lv.name,
                        "defaultValue": lv.props.config.defaultValue,
                        "sensitiveData": lv.props.config.sensitiveData,
                        "type": lv.props.config.type,
                        "moduleId": lv.props.parentModule,
                        "readOnly": lv.props.config.readOnly
                      
                    };

                    exportObj.variables['modVariables'].push(varprops);
                });
            }
            if (newMod.props.config.outputs && newMod.props.config.outputs.length > 0) {
                delete newMod.props.config.outputs;
            }
            if (newMod.props.config.inputs && newMod.props.config.inputs.length > 0) {
                for (var inpu = 0; inpu < newMod.props.config.inputs.length; inpu++) {
                    delete newMod.props.config.inputs[inpu].defaultValue;
                }
            }
            newMod.props.config.inputs = newMod.props.config.inputs ? newMod.props.config.inputs : [];
            newMod.props.config.inputs.forEach(input => {
                input.sensitiveData = input.sensitiveData ? input.sensitiveData : false;
                delete input.sensitive;
            })
            newMod.props.config.outputs = newMod.props.config.outputs ? newMod.props.config.outputs : [];
            newMod.props.config.outputs.forEach(output => {
                output.sensitiveData = output.sensitiveData ? output.sensitiveData : false;
                delete output.sensitive;
            })

            for (const ver in mod.versions) {
                if (mod.versions.hasOwnProperty(ver)) {
                    //addPostProcessingCustom for DEVDES2-1422
                    //adding back addPostProcessingCustom 
                    const flow = this.addPostProcessingCustom(mod.versions[ver].flow);
                    for (let p = 0; p < flow.nodes.length; p++) {
                        // sensitiveData
                        let flownode = Utility.updateApplicationVariableSensitiveData(JSON.parse(JSON.stringify(flow.nodes[p])), serialized);
                        
                        if (exclude[flownode._class]) { 
                            continue;
                        }
                        if (flownode._class === "BeginNodeModel") {
                            newMod.flow.beginNode = flownode.id;
                        }
                        flownode.props = this.exportProps(flownode.props, serialized);
                        flownode.type = this.getType(flownode._class);
                        const outputs = this.getOutPuts(flownode);
                        if (flownode.type !== "TABULAR") {
                            flownode.props.config.outputs = outputs;
                        }
                        switch (flownode.type) {
                            case "TABULAR":
                                delete flownode.props.config.inputs;
                                flownode.props.config.outputTo = outputs;
                                delete flownode.props.config.outputs;
                                break;
                            case "INPUT":
                                const inputConfig = flownode.props.config;
                                let sensitivity = inputsToSpeechSensitivityMap.get(inputConfig.inputType);

                                if (flownode.props.config.analystClass) {
                                    if (flownode.props.config.analystClass.startsWith("app.") || flownode.props.config.analystClass.startsWith("mod.")) {
                                        flownode.props.config.analystClass = {
                                            "type": "VARIABLE",
                                            "value": flownode.props.config.analystClass
                                        };
                                    }
                                    else {
                                        flownode.props.config.analystClass = {
                                            "type": "VALUE",
                                            "value": flownode.props.config.analystClass
                                        };
                                    }
                                }
                                if (flownode.props.config.analystClass === "") {
                                    delete flownode.props.config.analystClass;
                                }
                                
                                if (!inputConfig.inputType) {
                                    inputConfig.inputType = inputTypes.NONE;
                                }
                                inputConfig.speechSensitivity = sensitivity ? sensitivity : 50;
                                if (inputConfig.maxIntentTimeout === null || inputConfig.maxIntentTimeout === "") delete inputConfig.maxIntentTimeout;
                                if (inputConfig.lengthOfSilence === null || inputConfig.lengthOfSilence === "") delete inputConfig.lengthOfSilence;
                                if (inputConfig.noInputTimeout === null || inputConfig.noInputTimeout === "") delete inputConfig.noInputTimeout;
                                if (inputConfig.dtmfTerminationKey === null || inputConfig.dtmfTerminationKey === "") delete inputConfig.dtmfTerminationKey;
                                if (inputConfig.dtmfInterDigitTimeout === null || inputConfig.dtmfInterDigitTimeout === "") delete inputConfig.dtmfInterDigitTimeout;

                                const { noInputTimeoutOverride } = inputConfig;
                                Object.entries(noInputTimeoutOverride ?? {}).forEach(([channel, override]) => {
                                    if (!override) {
                                        delete noInputTimeoutOverride[channel];
                                    }
                                });

                                if (!inputConfig.sensitiveData) {
                                    inputConfig.sensitiveData = false;
                                }
                                if (inputConfig.dtmfInterDigitTimeout) {
                                    inputConfig.dtmfInterDigitTimeout = Number(inputConfig.dtmfInterDigitTimeout);
                                }
                                if (inputConfig.lengthOfSilence) {
                                    inputConfig.lengthOfSilence = Number(inputConfig.lengthOfSilence);
                                }
                                if (inputConfig.maxIntentTimeout) {
                                    inputConfig.maxIntentTimeout = Number(inputConfig.maxIntentTimeout);
                                }
                                if (inputConfig.noInputTimeout) {
                                    inputConfig.noInputTimeout = Number(inputConfig.noInputTimeout);
                                }
                                if (!inputConfig.DTMF) {
                                    delete inputConfig.dtmfInterDigitTimeout;
                                    delete inputConfig.dtmfTerminationKey;
                                }
                                if(inputConfig.localIntents && inputConfig.localIntents.length > 0){
                                    const localIntentIds = inputConfig.localIntents;
                                    //const inputNames = this.getInputNames(serialized,localIntentIds);
                                    //inputConfig.localIntents = inputNames;
                                }
                                
                                break;
                            case "PROMPT":
                                if (flownode.props && flownode.props.hasOwnProperty("config")) {
                                    flownode.bargeable = flownode.props.config.bargeable;
                                    flownode.description = flownode.props.config.description;
                                    flownode.dynamic = flownode.props.config.dynamic;
                                    flownode.inputs = flownode.props.config.inputs ? flownode.props.config.inputs : [];
                                    flownode.repeatable = flownode.props.config.repeatable;
                                    if (flownode.props.config.dynamic) {
                                        flownode.dynamicPromptType = flownode.props.config.dynamicPromptType;
                                        flownode.allowFallbackDomain = flownode.props.config.allowFallbackDomain;
                                    }
                                    if (flownode.props.config.isExternalAudio) {
                                        flownode.dynamic = true;
                                        flownode.allowFallbackDomain = true;
                                        flownode.dynamicPromptType = flownode.props.config.externalAudioPromptType;
                                        if (flownode.props.config.externalAudioPromptType === "EXTERNAL_AUDIO") {
                                            flownode.externalAudioServiceUrl = flownode.props.config.externalAudioServiceUrl;
                                        }
                                        else {
                                            flownode.externalAudioVariable = flownode.props.config.externalAudioVariable;
                                        }
                                    }
                                    if (flownode.dynamicPromptType && flownode.dynamicPromptType === "TTP") flownode.ttpInterCharacterPauseMs = flownode.props.config.ttpInterCharacterPauseMs;
                                    flownode.parentComponentID = flownode.props.parentComponentID;
                                    
                                    var transcript = [];
                                    
                                    for (var ke = 0; ke < Object.keys(flownode.props.config.text).length; ke++) {
                                        var lang = Object.keys(flownode.props.config.text)[ke];
                                        var text = {
                                            language: lang,
                                            text: flownode.props.config.text[lang]
                                        };
                                        transcript.push(text);
                                    }
                                    flownode.transcript = transcript;
                                    
                                    // Prompt channel for Export
                                    if (
                                        flownode.props.config.channel
                                        && isOmniChannel
                                    ) {
                                        flownode.channel = this.getPromptChannelForExport(
                                            serialized,
                                            flownode.props.config
                                        );
                                        flownode.channelLayer = flownode.props.config.channelLayer;
                                    }

                                    delete flownode.props.config.channel;
                                    delete flownode.props.config.channelLayer;
                                }
                                delete flownode.props;
    
                                break;
                            // TODO???
                            // work with PE for desired export format
                            case 'PROMPT_REFERENCE':
                                if(flownode.props.config.referenceType === 'variable'){
                                    flownode.props.config.promptReferenceType = flownode.props.config.variableReferenceType;
                                }
                                delete flownode.props.config.referenceType
                                delete flownode.props.config.variableReferenceType
                                break;
                            case 'MODULE':
                                if (flownode.props.config.inputs !== undefined) {
                                    const inputs = flownode.props.config.inputs;
                                    inputs.forEach(input => {
                                        delete input.defaultValue;
                                    })
                                }
                                break;
                            case "SAVE_PROMPT":
                                if (flownode.props.config.playSavedPromptId) {
                                    var keys = Utility.getPlaySavedPrompts(serialized);
                                    for (var pk = 0; pk < keys.length; pk++) {
                                        if (keys[pk].value === flownode.props.config.playSavedPromptId) {
                                            flownode.props.config.key = keys[pk].name;
                                            delete flownode.props.config.playSavedPromptId;
                                            break;
                                        }
                                    }
                                }
                                break;
                            case "DTMF_MENU":
                                flownode.props.config.inputTimeOut[0].key = flownode.props.config.inputTimeOut[0].key.replace(" ", "");
                                if (flownode.props.config.dtmfTerminationKey === null || flownode.props.config.dtmfTerminationKey === "") delete flownode.props.config.dtmfTerminationKey;
                                if (flownode.props.config.dtmfInterDigitTimeout === null || flownode.props.config.dtmfInterDigitTimeout === "") delete flownode.props.config.dtmfInterDigitTimeout;
                                if (flownode.props.config.dtmfInterDigitTimeout) flownode.props.config.dtmfInterDigitTimeout = Number(flownode.props.config.dtmfInterDigitTimeout);
                                
                                break;
                            case "REPORTING":

                                if (flownode.props.config) {
                                    flownode.description = flownode.props.config.description;
                                    flownode.oncePerCall = flownode.props.config.oncePerCall ? flownode.props.config.oncePerCall : false;
                                    flownode.inputs = flownode.props.config.inputs;
                                    flownode.outputs = flownode.props.config.outputs;
                                    flownode.parentComponentID = flownode.props.parentComponentID;
                                    flownode.reportTags = [];
                                    if (flownode.props.config.billing) {
                                        var scriptPerformance = {
                                            name: "Script Performance",
                                            value: flownode.props.config.billing.value,
                                            type: "VALUE",
                                            key: flownode.props.config.billing.key
                                        };
                                        if (flownode.props.config.billing.key === "START") {

                                            if (flownode.props.config.billing.value) {
                                                var spObj = flownode.props.config.billing.value;
                                                const scope = spObj.split(".")[0];
                                                var varName = spObj.split(".")[1];
                                                if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                                    scriptPerformance.type = "VARIABLE";
                                                }
                                            }
                                            flownode.reportTags.push(scriptPerformance);
                                        }
                                        else if (flownode.props.config.billing.key === "END") {
                                            var billing = {
                                                name: "Billing",
                                                value: flownode.props.config.billing.value,
                                                type: "VALUE",
                                                key: null
                                            };
                                            if (flownode.props.config.billing.value) {
                                                var argObj = flownode.props.config.billing.value;
                                                const scope = argObj.split(".")[0];
                                                if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                                    scriptPerformance.type = "VARIABLE";
                                                    billing.type = "VARIABLE";
                                                }
                                            }
                                            flownode.reportTags.push(scriptPerformance);
                                            flownode.reportTags.push(billing);
                                        }
                                        else if (flownode.props.config.billing.key === "FAILED") {
                                            if (flownode.props.config.billing.value) {
                                                var scriptPerformanceObj = flownode.props.config.billing.value;
                                                const scope = scriptPerformanceObj.split(".")[0];
                                                if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                                    scriptPerformance.type = "VARIABLE";
                                                }
                                            }
                                            flownode.reportTags.push(scriptPerformance);
                                        }
                                    }
                                    if (flownode.props.config.customTag && flownode.props.config.customTag.key !== "" && flownode.props.config.customTag.value !== "") {
                                        var customTag = {
                                            name: (flownode.props.config.customTag.key !== undefined && flownode.props.config.customTag.key !== "") ? flownode.props.config.customTag.key : "customTag",
                                            value: flownode.props.config.customTag.value,
                                            key: null,
                                            type: "VALUE"
                                        };
                                        if (flownode.props.config.customTag.value) {
                                            var cusObj = flownode.props.config.customTag.value;
                                            const scope = cusObj.split(".")[0];
                                            if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                                customTag.type = "VARIABLE";
                                            }
                                        }
                                        flownode.reportTags.push(customTag);
                                    }
                                    if (flownode.props.config.scriptProgress) {
                                        var scriptProgress = {
                                            name: "Script Progress",
                                            value: flownode.props.config.scriptProgress,
                                            type: "VALUE",
                                            key: null
                                        };
                                        var scriptProgressObj = flownode.props.config.scriptProgress;
                                        const scope = scriptProgressObj.split(".")[0];
                                        if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                            scriptProgress.type = "VARIABLE";
                                        }
                                        flownode.reportTags.push(scriptProgress);
                                    }
                                    if (flownode.props.config.transferDestination) {
                                        var transferDestination = {
                                            name: "Transfer Destination",
                                            value: flownode.props.config.transferDestination,
                                            type: "VALUE",
                                            key: null
                                        };
                                        var tdObj = flownode.props.config.transferDestination;
                                        const scope = tdObj.split(".")[0];
                                        if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                            transferDestination.type = "VARIABLE";
                                        }
                                        flownode.reportTags.push(transferDestination);
                                    }
                                    if (flownode.props.config.transferReason) {
                                        var transferReason = {
                                            name: "Transfer Reason",
                                            value: flownode.props.config.transferReason,
                                            type: "VALUE",
                                            key: null
                                        };
                                        var trObj = flownode.props.config.transferReason;
                                        const scope = trObj.split(".")[0];
                                        if (scope === Constant.k.SCOPE_APPLICATION || scope === Constant.k.SCOPE_MODULE) {
                                            transferReason.type = "VARIABLE";
                                        }
                                        flownode.reportTags.push(transferReason);
                                    }
                                }
                                delete flownode.props;
                                break;
                            case "CUSTOM":
                                if (flownode.props.config.functionType === "REUSEABLE") {
                                    if (flownode.props.config.function) {
                                        delete flownode.props.config.function.outputs;
                                        delete flownode.props.config.function.arguments;
                                    }
                                    if (flownode.props.config.function && flownode.props.config.function.ref !== undefined) {
                                        var refId = flownode.props.config.function.ref;
                                        flownode.props.config.function.script = "";
                                        if (serialized.functions && serialized.functions.length !== 0) {
                                            for (var h = 0; h < serialized.functions.length; h++) {
                                                if (refId === serialized.functions[h].id) {
                                                    var param = "";
                                                    if (flownode.props.config.inputs && flownode.props.config.inputs.length !== 0) {
                                                        for (var g = 0; g < flownode.props.config.inputs.length; g++) {
                                                            param += flownode.props.config.inputs[g].argumentName;
                                                            if (flownode.props.config.inputs.length > 1 && g !== flownode.props.config.inputs.length - 1) {
                                                                param += ",";
                                                            }
                                                        }
                                                        //
                                                        flownode.props.config.function.script += "return " + (serialized.repoApplication.replace(/ /g, "")).toLowerCase() + "$" + serialized.functions[h].name.toLowerCase() + "(" + param + ");";
                                                    }
                                                    else {
                                                        // 
                                                        flownode.props.config.function.script = "return " + (serialized.repoApplication.replace(/ /g, "")).toLowerCase() + "$" + serialized.functions[h].name.toLowerCase() + "();";
                                                    }
                                                }
                                            }
                                        }
                                    }

                                }
                                if (flownode.props.config.functionType === "CUSTOM") {
                                    if (flownode.props.config.function) {
                                        delete flownode.props.config.function.outputs;
                                        delete flownode.props.config.function.arguments;
                                    }
                                    if (serialized.functions && serialized.functions.length !== 0) {
                                        for (let fu = 0; fu < serialized.functions.length; fu++) {
                                            if (flownode.props.config.function && flownode.props.config.function.script) {
                                                let fun = flownode.props.config.function.script.includes(serialized.functions[fu].name + "(");
                                                if (fun === true) {
                                                    //
                                                    let replaceStr = serialized.repoApplication.replace(/ /g, "").toLowerCase() + "$" + serialized.functions[fu].name.toLowerCase() + "\(";
                                                    flownode.props.config.function.script = flownode.props.config.function.script.replace(new RegExp("\\b" + serialized.functions[fu].name + "\\\(", 'g'), replaceStr);
                                                }
                                            }
                                        }

                                    }
                                    addScript(flownode);
                                }
                                break;
                            case "DATA_ACCESS":
                                flownode.description = flownode.props.config.description;
                                flownode.parentComponentID = flownode.props.parentComponentID;
                                if (!flownode.props.config.asynchronous) {
                                    flownode.outputs = flownode.props.config.outputs;
                                }
                                flownode.headers = flownode.props.config.headers || []; 
                                flownode.schema = flownode.props.config.schema;
                                flownode.script = (flownode.props.config.script) ? flownode.props.config.script.replace(/\r?\n|\r/g, ' ') : flownode.props.config.script; // remove GraphQL linebreaks for PE
                                flownode.inputs = flownode.props.config.inputs;
                                flownode.asynchronous = flownode.props.config.asynchronous ? true : false;
                                flownode.customScript = !flownode.asynchronous ? flownode.props.config.customScript : "";

                                // [CORE-34184] httpRequestTimeout can be a number or a variable reference.
                                // In case it's a number we need to parse it and store it as an integer value
                                // so it's properly processed on PE (CORE-34185).
                                let httpRequestTimeoutVal = flownode.props.config.httpRequestTimeout;
                                if (!isNaN(httpRequestTimeoutVal)) {
                                    httpRequestTimeoutVal = parseInt(httpRequestTimeoutVal, 10);
                                }
                                flownode.httpRequestTimeout = httpRequestTimeoutVal;

                                delete flownode.props;
                                break;
                            case "START_SIMULTANEOUS_SESSION":
                                // hot-fix for simultaneous testing
                                delete flownode.props.config.channel; 
                                delete flownode.props.config.outputs;
                                delete flownode.props.config.disabledInFlow;
                                delete flownode.props.flowVersion;
                                break;
                            case "SIMULTANEOUS_SESSION_COMPLETION":
                                delete flownode.props.config.disabledInFlow;
                                delete flownode.props.flowVersion;
                                break;
                            case "RICH_TEXT": 
                                flownode = this.changeRichTextNodeToPEFormat(flownode);
                                break;
                            
                        }
                        // patch flownode inputs
                        if (flownode.inputs) {
                            flownode.inputs.forEach(this.normalizeInput);
                        }
                        if (flownode.props && flownode.props.config) {
                            // patch flownode inputs from config
                            if (flownode.props.config.inputs) {
                                flownode.props.config.inputs.forEach(this.normalizeInput);
                            }
                            // patch matchOrder from config
                            if (flownode.props.config.matchOrder) {
                                flownode.props.config.matchOrder.forEach(this.normalizeInput);
                            }
                        }
                        delete flownode._class;
                        delete flownode.x;
                        delete flownode.y;
                        newMod.flow.nodes.push(flownode);
                    }
                    for (var lin = 0; lin < flow.links.length; lin++) {
                        var link = flow.links[lin];
                        // hot fix to set sensitiveData on link input variables
                        const setSensitiveDataOnLinkInputVariables = (link) => {
                            if (link.props.config.inputs) {
                                link.props.config.inputs.forEach(input => {
                                    if (input.varName.startsWith('app.')) {
                                        var appVariableFound = serialized.components.variables.find(v => v.name === input.varName.substring(4));
                                        input.sensitiveData = (appVariableFound) ? serialized.components.variables.find(v => v.name === input.varName.substring(4)).props.config.sensitiveData : false;
                                    }
                                    if (input.varName.startsWith('mod.')) {
                                        var modVariableFound = newMod.localVariables.find(v => v.name === input.varName.substring(4));
                                        input.sensitiveData = (modVariableFound) ? newMod.localVariables.find(v => v.name === input.varName.substring(4)).props.config.sensitiveData : false;
                                    }
                                });
                            }
                            return;
                        };
                        
                        setSensitiveDataOnLinkInputVariables(link);

                        link.props = this.exportProps(link.props, serialized);
                        link.fromNodeId = link.source;
                        link.toNodeId = link.target;
                        link.name = link.props.config.name;
                        
                        if (serialized.functions && serialized.functions.length !== 0) {
                            for (var x = 0; x < serialized.functions.length; x++) {
                                if (link.props.config.serializedCondition) {
                                    var y = link.props.config.serializedCondition.includes(serialized.functions[x].name + "(");
                                    if (y === true) {
                                        // 
                                        var replaceString = serialized.repoApplication.replace(/ /g, "").toLowerCase() + "$" + serialized.functions[x].name.toLowerCase();
                                        link.props.config.serializedCondition = link.props.config.serializedCondition.replace(new RegExp(serialized.functions[x].name, 'gi'), replaceString);
                                    }
                                }
                            }
                        }

                        addScript(link);
                        delete link._class;
                        delete link.source;
                        delete link.target;
                        delete link.sourcePort;
                        delete link.targetPort;
                        delete link.points;
                        delete link.props;
                        newMod.flow.connections.push(link);
                    }
                }
            }

            exportObj.components.push(newMod);
        }

        // DEVDES2-1001: top-level script key for export
        exportObj.scripts = {
            customFunctions: Object.values(scripts.customFunctions),
            transitionFunctions: Object.values(scripts.transitionFunctions)
        };

        for (let i = 0; i < serialized.components.intents.length; i++) {
            var intent =  JSON.parse(JSON.stringify(serialized.components.intents[i]));
            intent.props = this.exportProps(intent.props, serialized);
            // CORE-36247: Adding sensitiveData to intents since inputType property requires it in PE
            if(intent.props.config.inputType){
                intent.props.config['sensitiveData'] = false;
            }
            intent.type = this.getType(intent._class);
            delete intent._class;
            delete intent.x;
            delete intent.y;

            exportObj.components.push(intent);
        }

        var promptObjects = _.filter(serialized.components.objects, { _class: "PromptNodeModel" });

        for (let i = 0; i < promptObjects.length; i++) {
            const prompt = Utility.updateApplicationVariableSensitiveData(promptObjects[i], serialized);
            //DEVDES2-1403
            if (prompt.props.config.externalAudioPromptType === "EXTERNAL_AUDIO") {
                delete prompt.props.config.externalAudioVariable;
             }
             else {
                 delete prompt.props.config.externalAudioServiceUrl;
             }

            //DEVDES2-1238
            if (prompt.props.config.isExternalAudio) {
                prompt.props.config.dynamic = true;
                prompt.props.config.dynamicPromptType = prompt.props.config.externalAudioPromptType;
                delete prompt.props.config.externalAudioPromptType;
                delete prompt.props.config.externalAudioUrlVarName;
            }
            //DEVDES2-965: Add "unused" key to prompts in design.json (remove from export)
            if(prompt.hasOwnProperty('unused')){
                delete prompt.unused;
            }

            // Prompt channel for export
            if (
                prompt.props.config.channel
                && isOmniChannel
            ) {
                prompt.channel = this.getPromptChannelForExport(
                    serialized,
                    prompt.props.config
                );
                prompt.channelLayer = prompt.props.config.channelLayer;
            }

            delete prompt.props.config.channel;
            delete prompt.props.config.channelLayer;
            exportObj.components.forEach(component => {
                if (component.type === "MODULE") {
                    component.flow.nodes.forEach(node => {
                        if (node.type === "PROMPT" && node.parentComponentID === prompt.id) {
                            prompt.props.config.inputs = node.inputs;
                        }
                    });
                }
            });
            
            prompt.props = this.exportProps(prompt.props, serialized);
            prompt.type = this.getType(prompt._class);
            delete prompt.props.parentModule;
            delete prompt._class;
            delete prompt.x;
            delete prompt.y;
         
            exportObj.components.push(prompt);
        }

        for (var va = 0; va < serialized.components.variables.length; va++) {

            const variable = JSON.parse(JSON.stringify(serialized.components.variables[va]));
            if (variable.props.config.type === "PROMPT" || variable.props.config.type === "MODULE") {
                variable.props.config.type = "STRING";
            }
            variable.props = this.exportProps(variable.props, serialized);
            delete variable.props.parentModule;
            
            variable.type = this.getType(variable._class);
            variable.props.config.readOnly = variable.props.config.constant ? variable.props.config.constant : false;
            if(serialized.appProperties.variable && serialized.appProperties.filter(properties => properties.variable.includes(variable.name)).length > 0) {
                variable.props.config.readOnly = true;
            }
            delete variable._class;
            delete variable.x;
            delete variable.y;
            exportObj.components.push(variable);
            var varprops = {

                "id": variable.id,
                "name": variable.name,
                "defaultValue": variable.props.config.defaultValue,
                "scope": variable.props.config.scope,
                "sensitiveData": variable.props.config.sensitiveData,
                "type": variable.props.config.type === "variable" ? "STRING" : variable.props.config.type,
                "readOnly": variable.props.config.readOnly
              
            };
            if(variable.type === "variableBinding" && serialized.appProperties.filter(properties => properties.variable.includes(variable.name)).length > 0) {
                varprops.readOnly = true;
            }
            
            // conditional appVariales handling for OmniChannel
            if(exportObj.isOmniChannel){
                if(variable.props.config.scope === "session"){
                    exportObj.variables['appVariables'].push(varprops);
                }
                if(variable.props.config.scope === "conversation"){
                    exportObj.variables['convVariables'].push(varprops);
                }
            } else {

                exportObj.variables['appVariables'].push(varprops);
            }
            
        }

        // DEVDES2-675: Supply BOTH 'sensitive' and 'sensitiveData' for inputs/outputs to nodes/modules
        exportObj.components.forEach(comp => {
            if (comp.type === "MODULE") {
                if (comp.props.config && comp.props.config.inputs) {
                    comp.props.config.inputs.forEach(input => {
                        input['sensitiveData'] = input['sensitiveData'] || false;
                        
                    });
                }
                comp.flow.nodes.forEach(fNode => {
                    
                    if (fNode.props) {
                        if (fNode.props.config.hasOwnProperty('inputs') && fNode.props.config.inputs !== undefined) {
                            fNode.props.config.inputs.forEach(input => {
                                input['sensitiveData'] = input['sensitiveData'] || false;
                                delete input['sensitive'];
                            });
                        }
                        if (fNode.props.config.hasOwnProperty('outputs') && fNode.props.config.outputs !== undefined) {
                            fNode.props.config.outputs.forEach(output => {
                                output['sensitiveData'] = output['sensitiveData'] || false;
                                delete output['sensitive'];
                            });
                        }
                        if (fNode.props.config.hasOwnProperty('matchOrder') && fNode.props.config.matchOrder !== undefined) {
                            fNode.props.config.matchOrder.forEach(mo => {
                                mo['sensitiveData'] = mo['sensitiveData'] || false;
                                delete mo['sensitive'];
                            });
                        }
                    } else { // e.g., DATA_ACCESS, REPORTING, PROMPT 
                        if (fNode.hasOwnProperty('inputs') && fNode.inputs !== undefined) {
                            fNode.inputs.forEach(input => {
                                input['sensitiveData'] = input['sensitiveData'] || false;
                                delete input['sensitive'];
                            });
                        }
                        if (fNode.hasOwnProperty('outputs') && fNode.outputs !== undefined) {
                            fNode.outputs.forEach(output => {
                                output['sensitiveData'] = output['sensitiveData'] || false;
                                delete output['sensitive'];
                         });
                        }
                    }
                });
            }
            if (comp.type === 'PROMPT') {
                if (comp.props) {
                    if (comp.props.config.hasOwnProperty('inputs') && comp.props.config.inputs !== undefined) {
                        comp.props.config.inputs.forEach(input => {
                            input['sensitiveData'] = input['sensitiveData'] || false;
                            delete input['sensitive'];
                        });
                    }
                }
            }
        });

        for (let type in exportObj.fillerPrompts) {
            for (var df = 0; df < exportObj.fillerPrompts[type].length; df++) {
                if (type !== "typing") {
                    exportObj.fillerPrompts[type][df].bargeable = "DISABLED";
                }
                else if (type === "typing") {
                    exportObj.fillerPrompts[type][df].bargeable = "ENABLED";
                }
                delete exportObj.fillerPrompts[type][df].lastSavedChange;
                delete exportObj.fillerPrompts[type][df].lastLocalChange;
                delete exportObj.fillerPrompts[type][df].createdTime;
            }

        }

        exportObj.version = serialized.repoVersion;
        
        console.timeEnd(timeId);
        return exportObj;
    }
   
    /**
     * @param {DesignModuleNodeProps} props
     * @param {Design} serialized
     */
    exportProps(props, serialized) {
        props = JSON.parse(JSON.stringify(props));

        switch (props.nodeTypeName) {
            case NodeTypeName.module:
                if (props.isCommonModule) {
                    const search = (props.isRootModule) ? 'rootModule' : props.moduleId;
                    const {
                        repoName,
                        repoVersion
                    } = serialized.components.modules.find((mod) => {
                        return mod.id === search;
                    });
                    props.commonModuleName = repoName;
                    props.commonModuleVersion = repoVersion;
                }
                break;
            case NodeTypeName.withFiller:
                if (props.config.referenceNodeType) {
                    props.config.referenceNodeType = this.getType(props.config.referenceNodeType);
                }
                break;
        }

        delete props.updatedTime;
        delete props.nodeTypeName;
        delete props.colorClass;
        delete props.iconClass;
        delete props.error;
        delete props.configView;
        delete props.permission;
        delete props.usedIn;
        delete props.version;
        delete props.config.version;
        delete props.isRootModule;
        delete props.moduleApp;
        delete props.updatedTime;
        delete props.iconClass;
        delete props.readOnly;
        delete props.lastLocalChange;
        delete props.lastSavedChange;
        delete props.createdTime;
        delete props.timeAddedToFlow;
        delete props.timeAddedtoFlow;
        return props;
    }

    getType(className) {
        return nodeTypeMap[className];
    }

    getOutPuts(node) {
        if (node.props.config.outputs && node.props.config.outputs.length > 0) {
            node.props.config.outputs.forEach(output => {
                // DEVDES2-1366: type should be uppercase
                // https://git.interactions.net/ps-tooling/d2-export-schema/blob/develop/d2-export-schema-json/src/main/resources/json/subSchemas/variableType.schema.json
                output.type = output.type.toUpperCase(); 
                if (output.type && (output.type === "PROMPT" || output.type === "MODULE")) {
                    output.type = "STRING";
                }
                if (node.type === "TABULAR") {
                    var outPutIndex = node.props.config.outputTo.findIndex(ou => ou.varName === output.varName);
                    if (outPutIndex !== -1) {
                        output.argumentName = node.props.config.outputTo[outPutIndex].argumentName;
                        output.sensitiveData = node.props.config.outputTo[outPutIndex].sensitiveData;

                    }
                }
            });
            return node.props.config.outputs;
        }
        else return [];
    }

    getPlaySavedPrompts(serialized) {
        const nodes = serialized.components.objects;
        var playSavedPrompts = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i]._class === 'PlaySavedPromptNodeModel' && nodes[i].props.config.key !== "") {

                playSavedPrompts.push({
                    name: nodes[i].props.config.key,
                    value: nodes[i].id
                });
            }
        }
        return playSavedPrompts.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * @param {DesignModuleVersion['flow']} flow
     */
    addPostProcessingCustom(flow) {
        const dataAccessNodes = flow.nodes.filter(n => n._class === "DataNodeModel" && !n.props.config.asynchronous);
        const dataAccessCompletionNodes = flow.nodes.filter(n => n._class === "DataAccessCompletionNodeModel");
        if (dataAccessNodes.length > 0 || dataAccessCompletionNodes.length > 0) {
            dataAccessNodes.forEach(dan => {
                if (dan.props.config.customScript && dan.props.config.customScript !== "") {
                    const customId = this.replaceCustomId(dan.id);
                    const custom = {
                        "id": customId,
                        "_class": "CustomNodeModel",
                        "type": "object",
                        "name": `PARSE_${dan.name}`,
                        "props": {
                            "nodeTypeName": "Custom Script",
                            "colorClass": "green",
                            "iconClass": "cpu",
                            "error": {},
                            "config": {
                                "description": "Parse the response form lookup account",
                                "functionType": "CUSTOM",
                                "function": {
                                    "script": dan.props.config.customScript
                                },
                                "disabledInFlow": false
                            },
                            "permission": {},
                            "createdTime": 1586973991202,
                            "parentComponentID": customId, // same as custom.id
                            "lastLocalChange": 1586973991973,
                            "timeAddedToFlow": 1586973991203
                        }
                    
                    };
                    flow.nodes.push(custom);
                    const sourceLinks = flow.links.filter(li => li.source === dan.id);
                    sourceLinks.forEach(sl => {
                        sl.source = custom.id;
                    });
                    const link = {
                        "id": Utility.uuid4(),
                        "_class": "BaseLinkModel",
                        "source": dan.id,
                        "sourcePort": "bottom",
                        "target": custom.id,
                        "targetPort": "top",
                        "points": [
                            {
                                "x": 930,
                                "y": 61
                            },
                            {
                                "x": 1190,
                                "y": 180
                            }
                        ],
                        "props": {
                            "nodeTypeName": "Transition",
                            "colorClass": "primary",
                            "error": {},
                            "config": {
                                "condition": [],
                                "serializedCondition": "true",
                                "name": "",
                                "description": "",
                                "inputs": [],
                                "disabledInFlow": false
                            },
                            "permission": {},
                            "lastLocalChange": 1586968671000,
                            "lastSavedChange": 1586968671000
                        }
                    };
                    flow.links.push(link);
                }
            });
            dataAccessCompletionNodes.forEach(dacn => {
                if (dacn.props.config.customScript && dacn.props.config.customScript !== "") {
    
                    const customId = this.replaceCustomId(dacn.id);
                    const custom = {
                        "id": customId,
                        "_class": "CustomNodeModel",
                        "type": "object",
                        "name": `PARSE_${dacn.name}`,
                        "props": {
                            "nodeTypeName": "Custom Script",
                            "colorClass": "green",
                            "iconClass": "cpu",
                            "error": {},
                            "config": {
                                "description": "Parse the response form lookup account",
                                "functionType": "CUSTOM",
                                "function": {
                                    "script": dacn.props.config.customScript
                                },
                                "disabledInFlow": false
                            },
                            "permission": {},
                            "createdTime": 1586973991202,
                            "parentComponentID": customId,
                            "lastLocalChange": 1586973991973,
                            "timeAddedToFlow": 1586973991203
                        }
                    
                    };
                    flow.nodes.push(custom);
                    const sourceLinks = flow.links.filter(li => li.source === dacn.id);
                    sourceLinks.forEach(sl => {
                        sl.source = custom.id;
                    });
                    const link = {
                        "id": Utility.uuid4(),
                        "_class": "BaseLinkModel",
                        "source": dacn.id,
                        "sourcePort": "bottom",
                        "target": custom.id,
                        "targetPort": "top",
                        "points": [
                            {
                                "x": 930,
                                "y": 61
                            },
                            {
                                "x": 1190,
                                "y": 180
                            }
                        ],
                        "props": {
                            "nodeTypeName": "Transition",
                            "colorClass": "primary",
                            "error": {},
                            "config": {
                                "condition": [],
                                "serializedCondition": "true",
                                "name": "",
                                "description": "",
                                "inputs": [],
                                "disabledInFlow": false
                            },
                            "permission": {},
                            "lastLocalChange": 1586968671000,
                            "lastSavedChange": 1586968671000
                        }
                    };
                    flow.links.push(link);
                }
                });
        
        }
        return flow;
    }

    /**
     * @param {Design}
     */
    removeFillerPromptsTimeStamps({ fillerPrompts }) {
        fillerPrompts = JSON.parse(JSON.stringify(fillerPrompts));
        for (const type in fillerPrompts) {
            if (fillerPrompts.hasOwnProperty(type)) {
                for (var g = 0; g < fillerPrompts[type].length; g++) {
                    delete fillerPrompts[type][g].lastLocalChange;
                    delete fillerPrompts[type][g].lastSavedChange;
                    delete fillerPrompts[type][g].createdTime;
                }
            }
        }
        return fillerPrompts;
    }

    replaceCustomId(id) {
        if (typeof id !== "string") return;

        return id.replace(/\w{8}/, Constant.k.CUSTOM_ID_EXPORT);
    }

    updateLocalVariableSensitiveData(module, serialized) {
        var localVariables = module.localVariables;
        const nodes = module.versions[1].flow.nodes;
        nodes.forEach((node, n) => {
            if (node.props.config && node.props.config.inputs) {
                if (node._class === "PromptNodeModel") {
                    node = Utility.updateApplicationVariableSensitiveData(node, serialized);
                }
                for (var input = 0; input < node.props.config.inputs.length; input++) {
                    if (node.props.config.inputs[input].type === "variable" && node.props.config.inputs[input].varName&&node.props.config.inputs[input].varName.includes("mod.")) {
                        const varName = node.props.config.inputs[input].varName.split(".")[1];
                        const v = localVariables.find(l => l.name === varName);
                        if (v) {
                            node.props.config.inputs[input].sensitiveData = v.props.config.sensitiveData;
                            if (node._class === "CustomNodeModel") {
                                node.props.config.function.arguments[input].sensitiveData = v.props.config.sensitiveData;
                            }
                        }
                    }
                }
                if (node._class === "TabNodeModel") {
                    for (var mo = 0; mo < node.props.config.matchOrder.length; mo++) {
                        if (node.props.config.matchOrder[mo].type === "variable" && node.props.config.matchOrder[mo].varName.includes("mod.")) {
                            const varName = node.props.config.matchOrder[mo].varName.split(".")[1];
                            const v = localVariables.find(l => l.name === varName);
                            if (v) {
                                node.props.config.matchOrder[mo].sensitiveData = v.props.config.sensitiveData;
                            }
                        }
                    }
                }
            }
            if (node.props.config && node.props.config.outputs) {
                for (var output = 0; output < node.props.config.outputs.length; output++) {
                    if (node.props.config.outputs[output].varName && node.props.config.outputs[output].varName.includes("mod.")) {
                        const varName = node.props.config.outputs[output].varName.split(".")[1];
                        const v = localVariables.find(l => l.name === varName);
                        if (v) {
                            node.props.config.outputs[output].sensitiveData = v.props.config.sensitiveData;
                            if (node._class === "CustomNodeModel") {
                                node.props.config.function.outputs[output].sensitiveData = v.props.config.sensitiveData;
                            }
                        }
                    }
                }
            }
            if (node.props.config && node.props.config.outputTo) {
                for (var output = 0; output < node.props.config.outputTo.length; output++) {
                    if (node.props.config.outputTo[output].varName && node.props.config.outputTo[output].varName.includes("mod.")) {
                        const varName = node.props.config.outputTo[output].varName.split(".")[1];
                        const v = localVariables.find(l => l.name === varName);
                        if (v) {
                            node.props.config.outputTo[output].sensitiveData = v.props.config.sensitiveData;

                        }
                    }
                }
            }
        })
        return module;

    }

    /**
     * Remove all disabledInFlow modules from serialized.components.modules
     * @param {json} serialized
     * @return {json} modified serialized
     */
    deleteDisabledModules(serialized){
        const components = serialized.components;
        // TODO: map returns a new array, here `mod` is not used and we just want to update components with filter
        components.modules.map(mod => {
            components.modules = components.modules.filter(m => !m.props.config.disabledInFlow);
        });

        return serialized;
    }

    /**
     * Remove all disabledInFlow items from serialized.components.modules
     * @param {json} serialized
     * @return {json} modified serialized
     */
    deletedDisabledInFlowItems(serialized){
        const mods = serialized.components.modules;

        // TODO: map returns a new array, here just want to update mod flows, et=c
        mods.map(mod => {
            const flow = mod.versions[1].flow;
            flow.links = flow.links.filter(l => !l.props.config.disabledInFlow);
            flow.nodes = flow.nodes.filter(n => !n.props.config.disabledInFlow);
        });

        return serialized;
    }

     /**
     * Remove TimeStamps for CompoundIntents && GlobalIntents
     * @param {} data 
     */
    deleteTimeStamps(data) {
        data.forEach(d => {
            delete d.lastLocalChange;
            delete d.lastSavedChange;
            delete d.createdTime;
        });
        return data;
    }

    /**
     * Returns a Export-formatted channel object based on a D2 prompt channel config
     * @param {Design} serialized the @see Design to export.
     * @param {object} promptNodeConfig a prompt.props.config object 
     * @returns 
     */
    getPromptChannelForExport(serialized, promptNodeConfig) {
        let promptChannel = {};
        const { channels } = serialized.rootSettings;
        Object.keys(channels).forEach((channel) => {
            const value = promptNodeConfig.channel[channel];
            if (value) {
                // Rename voice channel to "voicePrompt" for PE (CORE-29533).
                const peChannel = (channel === DesignChannelType.voice) ? 'voicePrompt' : channel;
                promptChannel[peChannel] = (channel === DesignChannelType.voice) ? (
                    getPromptVoiceChannel({ promptNodeConfig })
                ) : value;
            }
        });

        return promptChannel;
    }

    /**
     * remove non-essential properties from exported globalIntent objects
     * move inputs as direct property of ModuleInputs
     * @param {array} globalIntents 
     */
    getGlobalHandling(globalIntents){
        globalIntents.forEach(gi => {
            if(gi.moduleProperties){
                const inputs = _.cloneDeep(gi.moduleProperties.props.config.inputs);
            
                dotProp.delete(gi.moduleProperties, 'id'); 
                dotProp.delete(gi.moduleProperties, 'name'); 
                dotProp.delete(gi.moduleProperties, 'type'); 
                dotProp.delete(gi.moduleProperties, '_class');
                dotProp.delete(gi.moduleProperties, 'props');
                gi.moduleProperties.inputs =  inputs? inputs:[];
                gi.moduleProperties.inputs.forEach(input => {
                    delete input.sensitive;
                })
            }
           
        });
        

        return globalIntents;
    }

    /**
     * Populate Global DTMF Handling array
     * @param {array} intents 
     */
     getGlobalDTMFHandling(intents){
        const globalDtmfArray = [];
        intents.forEach(intent => {
            if(intent.props.config.dtmfInput && intent.props.config.dtmfInput !== "None"){  
                const globalDtmf = {
                    "dtmfInput" : intent.props.config.dtmfInput,
                    "intent": intent.id,
                    "inputType": intent.props.config.inputType || "None"
                  };
                globalDtmfArray.push(globalDtmf);
            }
        });
        
        return globalDtmfArray;
    }

    getIsOrphanedLink(flow, link) {
        // if link source/target do not exist in nodes, or if source/target value is null delete from design
        var nodes = flow.nodes;
        var sourceNotFound = !link.source || nodes.findIndex(n => n.id === link.source) === -1;
        var targetNotFound = !link.target || nodes.findIndex(n => n.id === link.target) === -1;

        return (sourceNotFound || targetNotFound);

    }

    /**
     * Normalize input data according to DEVDES2-902
     * @param variable
     */
    normalizeInput(input) {
        if (input.type && input.type.toLowerCase() === "value") {
            input.varName = input.varName ? input.varName : null;
        }
    }

    getInputNames(serialized,localIntentIds){
        const inputNames = []
        const allIntents = serialized.components.intents;
        localIntentIds.forEach(intentId =>{
            const getIntent = allIntents.filter(intent => intent.id === intentId);

            inputNames.push(getIntent[0].name);
        });
        
        return inputNames;
    }

    /** Change Rich Text Node to use PE JSON format 
     * @param {object} richTextNode - rich text node object
     * TODO: Add option to change the format when node dataTYPE is for ENTITY
    */
    changeRichTextNodeToPEFormat(richTextNode) {
        const dataType = richTextNode.props.config.dataType;
        switch(dataType){
            case "INTENT":{
                if(richTextNode.props.config?.buttons){
                    const buttonsObjectPE = {intents: []};
                    richTextNode.props.config.buttons.forEach(button => {
                        const currentIntent = {intent: button.intent, label: button.label, url: button.url};
                        buttonsObjectPE.intents.push(currentIntent);
                    });
                    richTextNode.props.config.buttons = buttonsObjectPE;
                }
                break;
            }
        }

        return nodeRemoveNullish(richTextNode, ["maxIntentTimeout", "noInputTimeout"]);
    }
}

export default ExportManager;